import type {
  Bot,
  BotSection,
  ExpertGoalState,
  ExpertGoalStatus,
  Group,
  Me,
  Space,
  ThreadSnapshot,
} from "@dante/contracts";
import { Disclosure, NativeSelect, NativeSelectOption } from "@dante/ui-web";
import { useLingui } from "@lingui/react/macro";
import { Columns3, MessagesSquare, Package, Settings, Target } from "lucide-react";
import type {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
  SetStateAction,
} from "react";
import { useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { IconTooltip } from "../../components/IconTooltip";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "../../lib/bootstrap";
import type { BotsSidebarDensity } from "../../lib/bots-sidebar-density-pref";
import type { RosterDropAction, RosterDropTarget } from "./roster-drop";
import type { sidebarGroupsForSpaces } from "./space-sidebar";

/** Goals the fleet is still working on; finished, cancelled, and blocked goals stay out of the summary. */
const RUNNING_GOAL_STATUSES: ReadonlySet<ExpertGoalStatus> = new Set([
  "pending",
  "active",
  "verifying",
  "nudge_queued",
]);

/** How many running goals the roster names before the count alone carries the rest. */
const GOAL_PREVIEW_LIMIT = 4;

/**
 * Roster data the shell still hands over while the per-bot roster is being removed. The
 * unified chat renders none of it, so every field is optional: the shell's current call site
 * keeps type-checking until it stops passing them.
 */
type LegacyRosterProps = {
  sidebarGroups?: ReturnType<
    typeof sidebarGroupsForSpaces<Space["bots"][number], Space["groups"][number]>
  >;
  collapsedSidebarSections?: ReadonlySet<string>;
  botSections?: BotSection[];
  rosterDropKey?: string | null;
  setRosterDropKey?: (key: string | null) => void;
  rosterDropAction?: (target: RosterDropTarget, sameList: boolean) => RosterDropAction | null;
  applyRosterDrop?: (target: RosterDropTarget, sameList: boolean, botIds: string[]) => void;
  spaceMenuAnchor?: RefObject<HTMLElement | null>;
  setSpaceMenu?: (menu: { id: string; position: { x: number; y: number } }) => void;
  setRenameSectionTarget?: (target: { section: BotSection; spaceId: string }) => void;
  setDeleteSectionTarget?: (target: { section: BotSection; spaceId: string }) => void;
  toggleSidebarSection?: (key: string) => void;
  inGroup?: boolean;
  active?: Pick<Bot, "id"> | undefined;
  activeGroup?: Pick<Group, "id"> | undefined;
  botsSidebarDensity?: BotsSidebarDensity;
  dismissedRunErrorIds?: ReadonlySet<string>;
  displayedRunError?: string | null;
  draggedBotId?: string | null;
  setDraggedBotId?: (id: string | null) => void;
  botMenu?: { kind: "bot" | "group"; id: string } | null;
  setBotMenu?: Dispatch<SetStateAction<{ kind: "bot" | "group"; id: string } | null>>;
  reorderRosterBot?: (sourceId: string, targetId: string, groupBotIds: string[]) => void;
  activeSnapshot?: ThreadSnapshot | null;
  botMenuContent?: ReactNode;
};

export type SidebarRosterProps = {
  /** Every space this person can switch to, in the order the switcher lists them. */
  spaces: Space[];
  /** The space the shell is showing; its switcher entry is the current one. */
  bootstrapMe: Pick<Me, "spaceId"> | null | undefined;
  /** Persists the chosen space before the route changes, so the unified chat follows it. */
  openSpaceChat: (spaceId: string, path: string) => void;
  /** Expert goals still running; the roster summarizes them only while any are. */
  goals?: readonly ExpertGoalState[];
  mini?: boolean;
} & LegacyRosterProps;

/**
 * The sidebar's whole navigation: the space switcher, the unified chat, artifacts, the ticket
 * board when the server enables it, and settings. Running expert goals are summarized under it.
 */
export function SidebarRoster({
  spaces,
  bootstrapMe,
  openSpaceChat,
  goals,
  mini = false,
}: SidebarRosterProps) {
  const { t } = useLingui();
  const { pathname, search } = useLocation();
  const boardEnabled = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  const activeSpace = spaces.find((space) => space.id === bootstrapMe?.spaceId) ?? spaces[0];
  const settingsOpen = new URLSearchParams(search).has("settings");
  const runningGoals = (goals ?? []).filter((goal) => RUNNING_GOAL_STATUSES.has(goal.status));
  const settingsPath = `${pathname}?settings=general`;

  const open = (path: string) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (!activeSpace) return;
    event.preventDefault();
    openSpaceChat(activeSpace.id, path);
  };

  const destinations = [
    {
      key: "chat",
      href: "/app",
      label: t`Chat`,
      icon: MessagesSquare,
      current: pathname === "/app" && !settingsOpen,
    },
    {
      key: "artifacts",
      href: "/app/artifacts",
      label: t`Artifacts`,
      icon: Package,
      current: pathname.startsWith("/app/artifacts"),
    },
    ...(boardEnabled
      ? [
          {
            key: "board",
            href: "/app/board",
            label: t`Board`,
            icon: Columns3,
            current: pathname.startsWith("/app/board"),
          },
        ]
      : []),
    {
      key: "settings",
      href: settingsPath,
      label: t`Settings`,
      icon: Settings,
      current: settingsOpen,
    },
  ];

  return (
    <nav
      aria-label={t`Space navigation`}
      data-testid="sidebar-roster"
      className={mini ? "flex flex-col items-center gap-2 pt-1" : "flex flex-col gap-1"}
    >
      {!mini && activeSpace ? (
        spaces.length > 1 ? (
          <div className="px-0.5">
            <NativeSelect
              size="sm"
              aria-label={t`Space`}
              data-testid="space-switcher"
              value={activeSpace.id}
              onChange={(event) => {
                const next = event.target.value;
                if (next !== activeSpace.id) openSpaceChat(next, "/app");
              }}
              className="w-full"
            >
              {spaces.map((space) => (
                <NativeSelectOption key={space.id} value={space.id}>
                  {space.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        ) : (
          <span
            data-testid="space-switcher"
            dir="auto"
            className="dante-panel-label truncate px-3 pt-4 pb-3 text-muted-foreground"
          >
            {activeSpace.name}
          </span>
        )
      ) : null}
      <div className={mini ? "flex flex-col items-center gap-2" : "flex flex-col gap-0.5"}>
        {destinations.map((destination) => {
          const Icon = destination.icon;
          const link = (
            <a
              key={destination.key}
              href={destination.href}
              data-roster-destination={destination.key}
              aria-current={destination.current ? "page" : undefined}
              aria-label={mini ? destination.label : undefined}
              onClick={open(destination.href)}
              className={`${
                mini
                  ? "dante-navigation-link mx-auto flex size-12 items-center justify-center rounded-md"
                  : "dante-navigation-link flex w-full items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium"
              } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring ${
                destination.current
                  ? "bg-sidebar-accent text-foreground"
                  : "text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
              }`}
            >
              <Icon size={mini ? 19 : 17} strokeWidth={1.8} aria-hidden="true" />
              {mini ? null : <span className="truncate">{destination.label}</span>}
            </a>
          );
          return mini ? (
            <IconTooltip key={destination.key} label={destination.label}>
              {link}
            </IconTooltip>
          ) : (
            link
          );
        })}
      </div>
      {runningGoals.length > 0 ? (
        <div
          data-testid="roster-goals"
          className={mini ? "mt-1" : "mt-5 border-t border-sidebar-border pt-4"}
        >
          {mini ? (
            <span className="mx-auto flex size-9 items-center justify-center gap-0.5 rounded-full bg-sidebar-accent text-[11px] font-semibold text-foreground tabular-nums">
              <Target size={13} strokeWidth={1.8} aria-hidden="true" />
              <span className="sr-only">{t`Expert goals`} </span>
              {runningGoals.length}
            </span>
          ) : (
            <Disclosure
              summary={
                <span className="dante-panel-label flex w-full items-center gap-1.5 text-muted-foreground">
                  <Target size={12} strokeWidth={2} aria-hidden="true" />
                  {t`Expert goals`}
                  <span className="ms-auto rounded-full bg-sidebar-accent px-1.5 py-0.5 text-[11px] font-semibold text-foreground tabular-nums">
                    {runningGoals.length}
                  </span>
                </span>
              }
              summaryClassName="w-full px-2 py-1"
              contentClassName="mt-0.5 flex flex-col gap-0.5"
            >
              {runningGoals.slice(0, GOAL_PREVIEW_LIMIT).map((goal) => (
                <a
                  key={goal.id}
                  href="/app"
                  onClick={open("/app")}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px] text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
                >
                  <span
                    aria-hidden="true"
                    className="size-1.5 shrink-0 rounded-full bg-status-working"
                  />
                  <span className="min-w-0 flex-1 truncate" dir="auto">
                    {goal.expertName}
                  </span>
                </a>
              ))}
            </Disclosure>
          )}
        </div>
      ) : null}
    </nav>
  );
}
